import React, { useCallback, useEffect, useState } from 'react';

/**
 * JobsPanel — SYSTEM scheduled jobs (admin-scheduled-jobs slug): the
 * `scheduled_task` rows with no owner, registered by code at service start
 * (super-local-collect, the vendor billing sync, …). The end-user "Scheduled
 * Tasks" page is owner-scoped and can never show these; this is the same set
 * of verbs — see the last result, pause/resume, reschedule, delete — for the
 * ownerless rows, modelled on that page.
 *
 * Proxied to aveq-agent /admin/jobs/* via /api/jobs/* (raw agent shape:
 * {jobs:[…]}). Gated at App.js by `manage_jobs @ cluster`; the BFF re-gates,
 * so direct curl still 403s without the permission.
 *
 * The row carries the latest result (last_status / last_result_summary /
 * last_run_at / error_count / last_skip_reason); expanding a job also lists its
 * last runs (≤10) from `job_run`, which the scheduler's log sink writes for
 * ownerless tasks (newest 50 kept).
 *
 * Reschedule takes a PHRASE ("every weekday at 5pm"), like the user page: the
 * agent translates it with the same model + examples chat uses, the scheduler
 * validates + applies it, and the reply echoes what was understood — a
 * schedule edit that silently means something else is the failure worth a
 * round trip.
 */
export default function JobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);        // job id whose detail is expanded
  const [when, setWhen] = useState(null);        // {job, text, said, busy} — reschedule editor
  const [label, setLabel] = useState(null);      // {job, text, busy} — label editor
  const [runs, setRuns] = useState({});          // job id → {loading, rows, error}

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/api/jobs', { credentials: 'same-origin' });
      if (!r.ok) { setError(`/api/jobs → HTTP ${r.status}`); return; }
      setJobs(((await r.json()) || {}).jobs || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // The last runs, fetched when a row is expanded (and again on refresh while
  // it is open). At most 10 — the history is for "what has it been doing",
  // not an audit log; the scheduler keeps 50.
  const loadRuns = useCallback(async (jobId) => {
    setRuns((r) => ({ ...r, [jobId]: { ...(r[jobId] || {}), loading: true } }));
    try {
      const res = await fetch(`/api/jobs/${jobId}/runs?limit=10`, { credentials: 'same-origin' });
      if (!res.ok) { setRuns((r) => ({ ...r, [jobId]: { loading: false, rows: [], error: `HTTP ${res.status}` } })); return; }
      const rows = ((await res.json()) || {}).runs || [];
      setRuns((r) => ({ ...r, [jobId]: { loading: false, rows, error: null } }));
    } catch (e) {
      setRuns((r) => ({ ...r, [jobId]: { loading: false, rows: [], error: String(e.message || e) } }));
    }
  }, []);

  useEffect(() => { if (open != null) loadRuns(open); }, [open, jobs, loadRuns]);

  // Every write returns the agent's error envelope on failure; surface its
  // message rather than the bare status, since the reschedule ones are
  // written for the person ("I can't express that as a repeating schedule…").
  const call = async (url, method, body) => {
    const r = await fetch(url, {
      method, credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (r.ok) return r.status === 204 ? {} : r.json();
    let msg = `${method} ${url} → HTTP ${r.status}`;
    try { const j = await r.json(); if (j && j.error && j.error.message) msg = j.error.message; } catch (e) { /* keep status */ }
    throw new Error(msg);
  };

  const togglePause = async (job) => {
    try {
      await call(`/api/jobs/${job.id}`, 'PATCH', { enabled: !job.enabled });
      refresh();
    } catch (e) { window.alert(e.message); }
  };

  const remove = async (job) => {
    if (!window.confirm(
      `Delete system job "${job.name}"?\n\nIt stops running now. A code-registered job comes back ` +
      `at the next start of the service that registers it (with its schedule reset); pause it ` +
      `instead if you only want it quiet.`)) return;
    try {
      await call(`/api/jobs/${job.id}`, 'DELETE');
      refresh();
    } catch (e) { window.alert(e.message); }
  };

  const saveLabel = async () => {
    setLabel((l) => ({ ...l, busy: true }));
    try {
      await call(`/api/jobs/${label.job.id}`, 'PATCH', { description: label.text.trim() });
      setLabel(null);
      refresh();
    } catch (e) { window.alert(e.message); setLabel((l) => ({ ...l, busy: false })); }
  };

  const saveWhen = async () => {
    setWhen((w) => ({ ...w, busy: true, said: null }));
    try {
      const res = await call(`/api/jobs/${when.job.id}/schedule`, 'POST', { when: when.text.trim() });
      // Keep the editor open with the echo, so the operator READS what the
      // scheduler understood before moving on. The row refreshes underneath.
      setWhen((w) => ({ ...w, busy: false, said: `${res.cadence_english} — ${res.said || ''}`.trim() }));
      refresh();
    } catch (e) { setWhen((w) => ({ ...w, busy: false, said: e.message })); }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Scheduled jobs</h2>
        <button onClick={refresh} style={refreshBtnStyle}>refresh</button>
      </div>
      <div style={{ color: '#888', fontSize: '0.78rem', marginBottom: 16 }}>
        Jobs the system runs on a schedule — not a user's scheduled tasks (those live on each
        user's own Scheduled Tasks page).
      </div>

      {loading && <div style={{ color: '#888' }}>Loading…</div>}
      {error && <div style={{ color: '#c00' }}>{error}</div>}

      {!loading && !error && (
        jobs.length === 0 ? (
          <div style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>
            No system jobs registered.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr style={trHeadStyle}>
                <th style={thStyle}>Job</th>
                <th style={thStyle}>Runs</th>
                <th style={thStyle}>Next</th>
                <th style={thStyle}>Last run</th>
                <th style={thStyle}>Result</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <React.Fragment key={job.id}>
                  <tr style={{ ...trStyle, opacity: job.enabled ? 1 : 0.55 }} data-testid={`job-${job.id}`}>
                    <td style={tdStyle}>
                      <button onClick={() => setOpen(open === job.id ? null : job.id)}
                        aria-expanded={open === job.id}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                 textAlign: 'left', font: 'inherit' }}>
                        <span style={{ fontWeight: 600 }}>{job.description || job.name}</span>
                        {job.description && (
                          <span style={{ color: '#999', marginLeft: 6, fontSize: '0.75rem' }}>{job.name}</span>
                        )}
                      </button>
                      <div style={{ color: '#888', fontSize: '0.75rem' }}>
                        {actionLabel(job.action_spec)}
                        {!job.enabled && <span style={{ ...pillStyle, background: '#eee', color: '#666', marginLeft: 6 }}>paused</span>}
                        {job.running_since && <span style={{ ...pillStyle, background: '#e8f4ff', color: '#1a5fb4', marginLeft: 6 }}>running</span>}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div>{job.cadence_english || job.schedule}</div>
                      <div style={{ color: '#aaa', fontSize: '0.72rem', fontFamily: 'monospace' }}>{job.schedule}</div>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{job.enabled ? fmt(job.next_run_at) : '—'}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmt(job.last_run_at)}</td>
                    <td style={tdStyle}>
                      <StatusPill status={job.last_status} errors={job.error_count} />
                      {job.last_skip_reason && (
                        <div style={{ color: '#b45309', fontSize: '0.75rem' }} title={fmt(job.last_skip_at)}>
                          skipped: {job.last_skip_reason}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <button onClick={() => togglePause(job)} style={smallBtnStyle}>
                        {job.enabled ? 'pause' : 'resume'}
                      </button>
                      <button onClick={() => setWhen({ job, text: '', said: null, busy: false })} style={smallBtnStyle}>
                        reschedule
                      </button>
                      <button onClick={() => setLabel({ job, text: job.description || '', busy: false })} style={smallBtnStyle}>
                        label
                      </button>
                      <button onClick={() => remove(job)} style={dangerBtnStyle}>delete</button>
                    </td>
                  </tr>
                  {open === job.id && (
                    <tr style={trStyle}>
                      <td colSpan={6} style={{ ...tdStyle, background: '#fafafa', paddingLeft: 28 }}>
                        <ActionSpec spec={job.action_spec} />
                        <div style={{ fontSize: '0.78rem', color: '#666', margin: '12px 0 4px' }}>
                          Last result{job.last_run_at ? ` · ${fmt(job.last_run_at)}` : ''}
                          {job.error_count > 0 && ` · ${job.error_count} consecutive error${job.error_count === 1 ? '' : 's'}`}
                        </div>
                        <pre data-testid={`job-result-${job.id}`} style={preStyle}>
                          {job.last_result_summary || (job.last_run_at ? '(no summary)' : '(has not run yet)')}
                        </pre>
                        <RunList state={runs[job.id]} jobId={job.id} />
                        <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 8 }}>
                          id {job.id} · sink {job.result_sink}{job.timeout_s ? ` · timeout ${job.timeout_s}s` : ''}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )
      )}

      {when && (
        <Modal title={`Reschedule "${when.job.description || when.job.name}"`} onClose={() => setWhen(null)}>
          <div style={{ fontSize: '0.82rem', color: '#666', marginBottom: 8 }}>
            Now: {when.job.cadence_english || when.job.schedule}. Say when it should run, in words.
          </div>
          <input autoFocus value={when.text} onChange={(e) => setWhen({ ...when, text: e.target.value })}
            placeholder="every weekday at 5pm · every 15 minutes · daily at 6am"
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            onKeyDown={(e) => { if (e.key === 'Enter' && when.text.trim() && !when.busy) saveWhen(); }} />
          {when.said && (
            <div data-testid="when-said" style={{ marginTop: 10, fontSize: '0.82rem', color: '#333',
                                                  background: '#f4f8ff', padding: '6px 10px', borderRadius: 4 }}>
              {when.said}
            </div>
          )}
          <div style={modalActionsStyle}>
            <button onClick={() => setWhen(null)} style={smallBtnStyle}>{when.said ? 'done' : 'cancel'}</button>
            <button onClick={saveWhen} disabled={!when.text.trim() || when.busy} style={primaryBtnStyle}>
              {when.busy ? 'updating…' : 'update schedule'}
            </button>
          </div>
        </Modal>
      )}

      {label && (
        <Modal title={`Label "${label.job.name}"`} onClose={() => setLabel(null)}>
          <div style={{ fontSize: '0.82rem', color: '#666', marginBottom: 8 }}>
            A display name for this job. Leave empty to show the job's own name.
          </div>
          <input autoFocus value={label.text} onChange={(e) => setLabel({ ...label, text: e.target.value })}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !label.busy) saveLabel(); }} />
          <div style={modalActionsStyle}>
            <button onClick={() => setLabel(null)} style={smallBtnStyle}>cancel</button>
            <button onClick={saveLabel} disabled={label.busy} style={primaryBtnStyle}>save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- bits -----------------------------------------------------------

// The function the job runs, as a person reads it — not the action_spec JSON.
// `Bills.sync` on station 1385, then each param on its own line (a param whose
// value is an object/list is shown as compact JSON, since that IS its value).
function ActionSpec({ spec }) {
  if (!spec || typeof spec !== 'object') return null;
  const params = spec.params && typeof spec.params === 'object' ? Object.entries(spec.params) : [];
  return (
    <div data-testid="action-spec" style={{ fontSize: '0.82rem' }}>
      <div style={{ color: '#666', fontSize: '0.78rem', marginBottom: 2 }}>Runs</div>
      <div>
        <code style={codeStyle}>{spec.function_set}.{spec.function}</code>
        {spec.station_id ? <span style={{ color: '#666' }}> on station {spec.station_id}</span> : null}
        {spec.origin ? <span style={{ color: '#999' }}> · origin {spec.origin}</span> : null}
      </div>
      {params.length > 0 ? (
        <table style={{ borderCollapse: 'collapse', marginTop: 4, marginLeft: 12 }}>
          <tbody>
            {params.map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: '1px 10px 1px 0', color: '#666', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                <td style={{ padding: '1px 0', wordBreak: 'break-word' }}>
                  {v !== null && typeof v === 'object' ? <code style={codeStyle}>{JSON.stringify(v)}</code> : String(v)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ color: '#999', fontSize: '0.75rem', marginLeft: 12 }}>no parameters</div>
      )}
    </div>
  );
}

// The newest runs, indented under the result, newest first, at most 10.
function RunList({ state, jobId }) {
  const rows = (state && state.rows) || [];
  return (
    <div data-testid={`job-runs-${jobId}`} style={{ marginTop: 12 }}>
      <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>
        Last runs{rows.length ? ` · ${rows.length}` : ''}
      </div>
      {state && state.loading && !rows.length && <div style={{ color: '#999', fontSize: '0.78rem', marginLeft: 16 }}>Loading…</div>}
      {state && state.error && <div style={{ color: '#c00', fontSize: '0.78rem', marginLeft: 16 }}>{state.error}</div>}
      {state && !state.loading && !state.error && rows.length === 0 && (
        <div style={{ color: '#999', fontSize: '0.78rem', fontStyle: 'italic', marginLeft: 16 }}>
          No runs recorded yet — history starts with the first run after this version.
        </div>
      )}
      {rows.length > 0 && (
        <ol style={{ margin: 0, paddingLeft: 32, fontSize: '0.8rem' }}>
          {rows.slice(0, 10).map((r) => (
            <li key={r.id} style={{ padding: '2px 0', borderBottom: '1px dotted #eee' }}>
              <span style={{ color: '#666', whiteSpace: 'nowrap', marginRight: 8 }}>{fmt(r.fired_at)}</span>
              <StatusPill status={r.status} errors={0} />
              <span style={{ marginLeft: 8, wordBreak: 'break-word' }}>
                {r.status === 'ok' ? (r.message_text || <em style={{ color: '#999' }}>no summary</em>)
                                   : (r.error_text || <em style={{ color: '#999' }}>no error text</em>)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function actionLabel(spec) {
  if (!spec || typeof spec !== 'object') return '';
  const fs = spec.function_set || '';
  const fn = spec.function || '';
  const st = spec.station_id ? ` @ station ${spec.station_id}` : '';
  return fs || fn ? `${fs}${fs && fn ? '.' : ''}${fn}${st}` : '';
}

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function StatusPill({ status, errors }) {
  if (!status) return <span style={{ color: '#999', fontSize: '0.78rem' }}>not yet run</span>;
  const ok = status === 'ok';
  return (
    <span style={{ ...pillStyle, background: ok ? '#e6f4ea' : '#fdecea', color: ok ? '#1e7e34' : '#a33' }}>
      {ok ? 'ok' : `error${errors > 1 ? ` ×${errors}` : ''}`}
    </span>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div role="dialog" aria-label={title} style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ ...smallBtnStyle, marginRight: 0 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---- styles (same values as UsersPanel, so the two tabs read as one UI) ----

const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' };
const trHeadStyle = { borderBottom: '2px solid #e0e0e0' };
const trStyle = { borderBottom: '1px solid #f0f0f0' };
const thStyle = { textAlign: 'left', padding: '6px 10px', color: '#666', fontWeight: 600 };
const tdStyle = { padding: '6px 10px', verticalAlign: 'top' };
const pillStyle = { borderRadius: 10, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600 };
const refreshBtnStyle = { background: 'none', border: '1px solid #ccc', borderRadius: 3, padding: '2px 8px', fontSize: '0.75rem', color: '#666', cursor: 'pointer' };
const smallBtnStyle = { background: 'none', border: '1px solid #ccc', borderRadius: 3, padding: '2px 8px', fontSize: '0.75rem', color: '#555', cursor: 'pointer', marginRight: 6 };
const dangerBtnStyle = { ...smallBtnStyle, color: '#a33', borderColor: '#e0b0b0' };
// admin-button-color: the SAME green as "+ Add Network" (NetworksView
// buttonPrimaryStyle), so every primary action in the admin reads as one.
const primaryBtnStyle = { background: '#5fa67a', border: 'none', borderRadius: 4, padding: '6px 14px', fontSize: '0.85rem', color: '#fff', cursor: 'pointer' };
const codeStyle = { fontSize: '0.78rem', background: '#f0f0f0', padding: '1px 5px', borderRadius: 3 };
const inputStyle = { padding: '4px 8px', fontSize: '0.82rem', border: '1px solid #ccc', borderRadius: 3 };
const modalActionsStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const modalStyle = { background: '#fff', borderRadius: 6, padding: '20px 22px', width: 460, maxWidth: '90vw', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' };
const preStyle = { margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem', fontFamily: 'inherit', maxHeight: 240, overflow: 'auto' };
